import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_HeightWidth = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📐 CSS Height and Width</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        In CSS, the <code>height</code> and <code>width</code> properties define
        the size of an element. By default, these properties set the size of the
        content area inside the element, not including padding, borders, or
        margins.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  height: value;
  width: value;
}`}
        />
      </div>

      {/* Example with fixed values */}
      <div className="tutorial-section">
        <h2 className="section-title">Fixed Height and Width</h2>
        <CodeCopyBlock
          code={`div {
  height: 150px;
  width: 300px;
  border: 2px solid #333;
}`}
        />
        <div
          style={{
            height: "150px",
            width: "300px",
            border: "2px solid #333",
            background: "#f1f5f9",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Fixed 300px × 150px box
        </div>
      </div>

      {/* Auto Value */}
      <div className="tutorial-section">
        <h2 className="section-title">Auto Value</h2>
        <p>
          The default value for both properties is <code>auto</code>, which lets
          the browser calculate the size automatically based on content.
        </p>
        <CodeCopyBlock
          code={`div {
  width: auto;
  height: auto;
}`}
        />
      </div>

      {/* Percentage Values */}
      <div className="tutorial-section">
        <h2 className="section-title">Percentage Values</h2>
        <p>
          Percentages are calculated relative to the parent element’s
          dimensions.
        </p>
        <CodeCopyBlock
          code={`div {
  width: 50%;  /* 50% of parent width */
  height: 50%; /* 50% of parent height */
}`}
        />
        <div
          style={{
            border: "2px dashed #0ea5e9",
            width: "400px",
            height: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "50%",
              height: "50%",
              background: "#bae6fd",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            50% box
          </div>
        </div>
      </div>

      {/* Max-width and Min-width */}
      <div className="tutorial-section">
        <h2 className="section-title">Max-Width and Min-Width</h2>
        <p>
          - <code>max-width</code> prevents an element from exceeding a maximum
          width. - <code>min-width</code> ensures an element has at least a
          minimum width.
        </p>
        <CodeCopyBlock
          code={`div {
  max-width: 500px;
  min-width: 200px;
}`}
        />
        <div
          style={{
            maxWidth: "500px",
            minWidth: "200px",
            background: "#dcfce7",
            padding: "10px",
            border: "2px solid #15803d",
          }}
        >
          Resize the window — my width is flexible but between 200px and 500px.
        </div>
      </div>

      {/* Max-height and Min-height */}
      <div className="tutorial-section">
        <h2 className="section-title">Max-Height and Min-Height</h2>
        <CodeCopyBlock
          code={`div {
  max-height: 300px;
  min-height: 100px;
}`}
        />
        <div
          style={{
            maxHeight: "300px",
            minHeight: "100px",
            overflow: "auto",
            background: "#fef3c7",
            border: "2px solid #f59e0b",
            padding: "10px",
          }}
        >
          This box has a <strong>min-height of 100px</strong> and{" "}
          <strong>max-height of 300px</strong>. If content exceeds 300px, it
          will scroll:
          <p>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec
            odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla
            quis sem at nibh elementum imperdiet.
          </p>
          <p>
            Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue
            semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla.
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you understand height and width, check out{" "}
          <a href="/css/boxmodel" className="learn-more">
            CSS Box Model
          </a>{" "}
          to see how size interacts with margins, padding, and borders.
        </p>
      </div>
    </div>
  );
};

export default CSS_HeightWidth;
